import * as Sentry from '@sentry/react'
import type { ErrorEvent } from '@sentry/react'
import React from 'react'
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router'

/**
 * Scrub known PII patterns from a string.
 * Replaces matches with [REDACTED] so we preserve the shape of the error
 * message while dropping the sensitive content.
 *
 * Patterns covered:
 * - Plaid access tokens (access-sandbox-*, access-development-*, access-production-*)
 * - Plaid public tokens (public-sandbox-*, public-development-*, public-production-*)
 * - Stripe secret keys (sk_live_*, sk_test_*)
 * - Stripe publishable keys are NOT scrubbed (they are public by design)
 * - BankSheets API keys (bk_*)
 * - JWT tokens (3 base64url segments separated by dots)
 * - Email addresses
 * - Bank account / routing numbers when labeled (e.g., "accountNumber": "12345678")
 *   — we deliberately do NOT scrub bare 8-17 digit numbers because they collide
 *   with timestamps, Stripe numeric IDs, retry counters, and other useful
 *   debugging context.
 */
const scrubPII = (str: string): string => {
  if (typeof str !== 'string') return str
  return str
    .replace(/access-(sandbox|development|production)-[a-zA-Z0-9-]+/gi, '[REDACTED_PLAID_TOKEN]')
    .replace(/public-(sandbox|development|production)-[a-zA-Z0-9-]+/gi, '[REDACTED_PLAID_TOKEN]')
    .replace(/sk_(live|test)_[A-Za-z0-9_]+/g, '[REDACTED_STRIPE_KEY]')
    .replace(/bk_[A-Za-z0-9]+/g, '[REDACTED_BANKSHEETS_KEY]')
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[REDACTED_JWT]')
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[REDACTED_EMAIL]')
    .replace(
      /(account[_-]?number|acct[_-]?number|routing[_-]?number)(["\s:=]+)["']?(\d{8,17})["']?/gi,
      '$1$2[REDACTED_ACCOUNT_NUMBER]'
    )
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_APP_VERSION || 'unknown',
  enabled: import.meta.env.PROD,

  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      // Banking app — mask all text and media by default to prevent leaking
      // sensitive financial data (account numbers, balances, transactions, emails)
      // to Sentry's dashboard. UI structure is still visible for debugging.
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  // Performance: sample 20% of transactions in production
  tracesSampleRate: 0.2,

  // Session Replay: record 10% of sessions, 100% on error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Propagate sentry-trace and baggage headers to our backend so frontend and
  // backend Sentry can produce end-to-end distributed traces once backend Sentry
  // lands. Without this, Sentry only propagates to same-origin URLs by default.
  tracePropagationTargets: [
    // Firebase Cloud Functions (current backend)
    /^https:\/\/.*\.cloudfunctions\.net/,
    // Cloud Run (future backend target)
    /^https:\/\/.*\.run\.app/,
    // Local development
    'localhost',
  ],

  // Filter out common browser noise.
  // Note: ChunkLoadError is intentionally NOT filtered here — for an SPA with
  // lazy-loaded routes, chunk load failures are how we discover stale deploys,
  // CDN propagation issues, and broken asset publishing. They're operationally
  // important, not noise. If they become overwhelming in production, filter
  // specific patterns via beforeSend instead of suppressing the entire class.
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    // Aborted fetches (user navigated away mid-request) — genuine cleanup, not bugs
    'AbortError',
    'The operation was aborted',
    // Browser extension errors
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
  ],

  // Drop errors originating from browser extensions only.
  // We deliberately do NOT filter Plaid, Stripe, PostHog, or GTM here —
  // those are core integrations and we need visibility when they break.
  // If those become noisy in production, filter specific error patterns
  // via beforeSend instead of suppressing the entire script.
  denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i, /^safari-extension:\/\//i],

  /**
   * Scrub known PII patterns from error events before sending to Sentry.
   *
   * This is a defensive second layer — the primary defense is not logging
   * sensitive data in the first place — but for a banking product we want
   * belt-and-suspenders protection against accidental leaks (e.g., a
   * developer logging a full user object, an error message containing a
   * raw API response, etc.).
   */
  beforeSend(event: ErrorEvent): ErrorEvent | null {
    // Scrub exception messages
    if (event.exception?.values) {
      event.exception.values = event.exception.values.map((exc) => ({
        ...exc,
        value: exc.value ? scrubPII(exc.value) : exc.value,
      }))
    }

    // Scrub top-level message
    if (event.message) {
      event.message = scrubPII(event.message)
    }

    // Scrub request URL query params that may contain tokens
    if (event.request?.url) {
      event.request.url = event.request.url.split('?')[0]
    }

    // Scrub extra context values
    if (event.extra) {
      for (const key of Object.keys(event.extra)) {
        const value = event.extra[key]
        if (typeof value === 'string') {
          event.extra[key] = scrubPII(value)
        }
      }
    }

    return event
  },
})
